<template>
  <menu-content-container
    :header="label || $t('forms.border_radius')"
    border-full
    :padding-left-right="paddingLeftRight"
  >
    <IndependentValue
      :header="label || $t('forms.border_radius')"
      :default-open="isMixed"
    >
      <template #independent-to-expand>
        <content-row class="!justify-end">
          <t-input-number
            v-model="borderRadiustl"
            data-testid="top-left-border-radius"
            :regex-pattern="'\d+'"
            :min="minY"
            :max="maxY"
            :label="$t('forms.top_left')"
            unit=""
            :changed="changedTopLeft"
            flex
            @reset="resetRadiusDirection('topLeft')"
          >
            <template #label>
              <SideRadius class="rotate-[270deg]" />
            </template>
          </t-input-number>
          <t-input-number
            v-model="borderRadiustr"
            data-testid="top-right-border-radius"
            :regex-pattern="'\d+'"
            :min="minX"
            :max="maxX"
            :label="$t('forms.top_right')"
            unit=""
            :changed="changedTopRight"
            flex
            @reset="resetRadiusDirection('topRight')"
          >
            <template #label>
              <SideRadius />
            </template>
          </t-input-number>
        </content-row>
        <content-row class="!justify-end">
          <t-input-number
            v-model="borderRadiusbl"
            data-testid="bottom-left-border-radius"
            :regex-pattern="'\d+'"
            :min="minX"
            :max="maxY"
            :label="$t('forms.bottom_left')"
            unit=""
            :changed="changedBottomLeft"
            flex
            @reset="resetRadiusDirection('bottomLeft')"
          >
            <template #label>
              <SideRadius class="rotate-[180deg]" />
            </template>
          </t-input-number>
          <t-input-number
            v-model="borderRadiusbr"
            data-testid="bottom-rigth-border-radius"
            :regex-pattern="'\d+'"
            :min="minY"
            :max="maxY"
            :label="$t('forms.bottom_right')"
            unit=""
            flex
            :changed="changedBottomRight"
            @reset="resetRadiusDirection('bottomRight')"
          >
            <template #label>
              <SideRadius class="rotate-[90deg]" />
            </template>
          </t-input-number>
        </content-row>
      </template>
      <TInputCombinated
        v-model="commonBorderRadius"
        :label="inputLabel || `${$t('forms.border_radius')}`"
        :min="min"
        :max="max"
        :disabled="disabled"
        :units="['px']"
        :width="40"
        :changed="changed"
        flex
        class="w-full"
        data-testid="common-border-radius"
        @reset="emits('reset')"
      />
    </IndependentValue>
  </menu-content-container>
</template>

<script lang="ts" setup>
  import IndependentValue from "./components/IndependentValue/IndependentValue.vue";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { resolveDisplayedSpacing } from "@/utils/spacingUtils";
  import { TInputNumber, TInputCombinated } from "@topol/twig-ui";
  import { SideRadius } from "@topol/icons";
  import { ref, computed, onMounted } from "vue";

  const props = defineProps({
    product: {
      type: Boolean,
      default: false,
    },
    prodstructure: {
      type: [Object, Boolean],
      default: null,
    },
    label: {
      type: String,
      default: "",
    },
    inputLabel: {
      type: String,
      default: "",
    },
    toggleLabel: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    prefix: {
      type: String,
      default: "",
    },
    externalUsage: {
      type: Boolean,
      default: false,
    },
    externalBorderRadius: {
      type: String,
      default: undefined,
    },
    minY: {
      type: Number,
      default: 0,
    },
    maxY: {
      type: Number,
      default: 99,
    },
    minX: {
      type: Number,
      default: 0,
    },
    maxX: {
      type: Number,
      default: 99,
    },
    changed: {
      type: Boolean,
      default: false,
    },
    defaultValue: {
      type: String,
      default: undefined,
    },
    paddingLeftRight: {
      type: Number,
      default: undefined,
    },
  });

  const emits = defineEmits(["borderRadiusChanged", "reset"]);

  const detailBorder = ref(false);

  // --- Per-direction default parsing ---
  function parseBorderRadiusParts(val: string | undefined) {
    if (!val) return { topLeft: 0, topRight: 0, bottomRight: 0, bottomLeft: 0 };
    const parts = val.split(" ").map((p) => parseInt(p) || 0);
    if (parts.length === 1)
      return {
        topLeft: parts[0],
        topRight: parts[0],
        bottomRight: parts[0],
        bottomLeft: parts[0],
      };
    if (parts.length === 2)
      return {
        topLeft: parts[0],
        topRight: parts[1],
        bottomRight: parts[0],
        bottomLeft: parts[1],
      };
    if (parts.length === 3)
      return {
        topLeft: parts[0],
        topRight: parts[1],
        bottomRight: parts[2],
        bottomLeft: parts[1],
      };
    return {
      topLeft: parts[0],
      topRight: parts[1],
      bottomRight: parts[2],
      bottomLeft: parts[3],
    };
  }

  const resolvedDefault = computed(() => props.defaultValue);
  const defaultRadiusParts = computed(() =>
    parseBorderRadiusParts(resolvedDefault.value)
  );

  // The radius actually on display, expanded once per change. Reusing
  // parseBorderRadiusParts keeps the displayed corners and defaultRadiusParts
  // in agreement (they are compared to decide whether a corner shows a reset)
  // and avoids re-reading the store and re-splitting the value per corner.
  const radiusParts = computed(() =>
    parseBorderRadiusParts(getBorderRadiusAttribute())
  );

  // Each corner is "changed" when it differs from its own default. When no
  // defaultValue is provided, defaultRadiusParts falls back to 0 for every
  // corner, so the reset only shows next to the corner that actually differs.
  const changedTopLeft = computed(
    () => borderRadiustl.value !== defaultRadiusParts.value.topLeft
  );
  const changedTopRight = computed(
    () => borderRadiustr.value !== defaultRadiusParts.value.topRight
  );
  const changedBottomRight = computed(
    () => borderRadiusbr.value !== defaultRadiusParts.value.bottomRight
  );
  const changedBottomLeft = computed(
    () => borderRadiusbl.value !== defaultRadiusParts.value.bottomLeft
  );

  function resetRadiusDirection(
    direction: "topLeft" | "topRight" | "bottomRight" | "bottomLeft"
  ) {
    const def = defaultRadiusParts.value;
    const tl = direction === "topLeft" ? def.topLeft : borderRadiustl.value;
    const tr = direction === "topRight" ? def.topRight : borderRadiustr.value;
    const br =
      direction === "bottomRight" ? def.bottomRight : borderRadiusbr.value;
    const bl =
      direction === "bottomLeft" ? def.bottomLeft : borderRadiusbl.value;
    setBorderRadius(tl + "px " + tr + "px " + br + "px " + bl + "px");
  }

  const mjmlStore = useMjmlStore();

  const min = computed(() => {
    if (props.minY < props.minX) {
      return props.minX;
    } else {
      return props.minY;
    }
  });

  const max = computed(() => {
    if (props.maxY < props.maxX) {
      return props.maxX;
    } else {
      return props.maxY;
    }
  });

  // Common field: shows a single value when all corners match, "Mixed" otherwise.
  // Returns a "px" string (not a bare number) so that 0 renders as "0" instead
  // of an empty input in TInputCombinated.
  const commonBorderRadius = computed<string>({
    get() {
      const tl = borderRadiustl.value || 0;
      const tr = borderRadiustr.value || 0;
      const br = borderRadiusbr.value || 0;
      const bl = borderRadiusbl.value || 0;
      if (tl === tr && tr === br && br === bl) {
        return `${tl}px`;
      }
      return "Mixed";
    },
    set(val) {
      // Setting the common field applies one value to every corner. Skip only
      // when all corners already equal it. Comparing against a single corner
      // (top-left) would wrongly treat a "Mixed" state whose top-left matches
      // as a no-op — e.g. top-left = 0 made the arrows dead while the other
      // corners still differed.
      const num = parseInt(String(val)) || 0;
      const tl = borderRadiustl.value || 0;
      const tr = borderRadiustr.value || 0;
      const br = borderRadiusbr.value || 0;
      const bl = borderRadiusbl.value || 0;
      if (tl === num && tr === num && br === num && bl === num) {
        return;
      }
      setBorderRadius(`${num}px`);
    },
  });

  // Auto-expand the per-corner detail when the corners differ ("Mixed").
  const isMixed = computed(() => commonBorderRadius.value === "Mixed");

  const borderRadiustl = computed({
    get() {
      return radiusParts.value.topLeft;
    },
    set(val: number) {
      if (val !== borderRadiustl.value) {
        if (val === undefined) {
          val = 0;
        }

        const setval =
          val +
          "px " +
          borderRadiustr.value +
          "px " +
          borderRadiusbr.value +
          "px " +
          borderRadiusbl.value +
          "px";
        setBorderRadius(setval);
      }
    },
  });

  const borderRadiustr = computed({
    get() {
      return radiusParts.value.topRight;
    },
    set(val: number) {
      if (val !== borderRadiustr.value) {
        if (val === undefined) {
          val = 0;
        }

        const setval =
          borderRadiustl.value +
          "px " +
          val +
          "px " +
          borderRadiusbr.value +
          "px " +
          borderRadiusbl.value +
          "px";
        setBorderRadius(setval);
      }
    },
  });

  const borderRadiusbr = computed({
    get() {
      return radiusParts.value.bottomRight;
    },
    set(val: number) {
      if (val !== borderRadiusbr.value) {
        if (val === undefined) {
          val = 0;
        }

        const setval =
          borderRadiustl.value +
          "px " +
          borderRadiustr.value +
          "px " +
          val +
          "px " +
          borderRadiusbl.value +
          "px";
        setBorderRadius(setval);
      }
    },
  });

  const borderRadiusbl = computed({
    get() {
      return radiusParts.value.bottomLeft;
    },
    set(val: number) {
      if (val !== borderRadiusbl.value) {
        if (val === undefined) {
          val = 0;
        }

        const setval =
          borderRadiustl.value +
          "px " +
          borderRadiustr.value +
          "px " +
          borderRadiusbr.value +
          "px " +
          val +
          "px";
        setBorderRadius(setval);
      }
    },
  });

  /**
   * Returns the border-radius to display: the stored override when there is
   * one, otherwise the effective default. Reads externalBorderRadius when
   * externalUsage is enabled, the active block attribute otherwise.
   */
  function getBorderRadiusAttribute() {
    const stored = props.externalUsage
      ? props.externalBorderRadius
      : mjmlStore.getActiveBlockAttrString(props.prefix + "border-radius");

    return resolveDisplayedSpacing(stored, props.defaultValue);
  }

  /**
   * Emmits event if using exetrnal usage.
   * Updates block attribute if external usage is not enabled.
   */
  function setBorderRadius(pad: string | undefined) {
    if (props.externalUsage) {
      emits("borderRadiusChanged", pad);
    } else {
      mjmlStore.updateBlockAttribute({
        prop: props.prefix + "border-radius",
        value: pad,
        product: props.product,
        prodstructure: props.prodstructure,
      });
    }
  }

  onMounted(() => {
    detailBorder.value = isMixed.value;

    if (
      !props.externalUsage &&
      !isMixed.value &&
      getBorderRadiusAttribute() === "0px"
    ) {
      setBorderRadius("0px");
    }
  });
</script>
